import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { fmtPHP } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const CHECK_ITEMS = [
  ["identity", "Name & phone", "Match the application to authorized verification records."],
  ["documents", "ID photos", "Check validity and consistency. Do not share images unnecessarily."],
  ["employment", "Address & employment", "Compare with permitted supporting documents."],
  ["income", "Monthly income", "Confirm income and assess the proposed repayments."],
  ["purpose", "Loan purpose", "Check that the purpose is documented and consistent."],
  ["bank", "Bank ownership", "Use the company’s approved ownership verification process."],
  ["credit", "Credit information", "Use lawful sources and record the authorization reference."],
];

export const ReviewStatusBadge = ({ status = "PENDING", testId }) => {
  const tone = status === "APPROVED SUCCESSFULLY" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
    : /REJECTED|INVALID|INSUFFICIENT/.test(status) ? "border-rose-500/30 bg-rose-500/10 text-rose-300"
    : status === "PENDING" ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
    : "border-sky-500/30 bg-sky-500/10 text-sky-200";
  return <span data-testid={testId} className={`inline-flex max-w-full rounded-md border px-2 py-1 text-[10px] font-bold tracking-wide whitespace-normal leading-relaxed ${tone}`}>{status}</span>;
};

export const ReviewSelect = ({ value, onChange, options, testId, label, disabled, allLabel = "All statuses" }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger aria-label={label} data-testid={testId} className="h-auto min-h-10 bg-[#0B111D] border-white/10 text-slate-100 text-xs [&>span]:whitespace-normal [&>span]:text-left [&>span]:line-clamp-none">
      <SelectValue placeholder={label} />
    </SelectTrigger>
    <SelectContent className="max-w-[calc(100vw-2rem)] bg-[#101A29] text-white border-white/10">
      {options.map((s, i) => <SelectItem value={s} key={s} data-testid={`${testId}-option-${i}`} className="text-xs whitespace-normal focus:bg-sky-500/15 focus:text-white">{s === "All" ? allLabel : s}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const ReviewPageHeader = ({ eyebrow, title, backTo, backLabel, children }) => (
  <header className="space-y-5">
    <Link to={backTo} data-testid="review-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-sky-300 transition-colors"><ArrowLeft className="h-4 w-4" />{backLabel}</Link>
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
      <div><div className="text-[10px] uppercase tracking-[0.24em] text-sky-400 font-bold">{eyebrow}</div><h1 className="text-4xl font-display font-bold text-white mt-2">{title}</h1></div>
      {children}
    </div>
  </header>
);

export const VerificationNotice = () => (
  <div data-testid="verification-privacy-notice" className="flex items-start gap-3 rounded-xl border border-sky-500/20 bg-sky-500/5 p-4 text-xs text-slate-300 leading-relaxed">
    <ShieldCheck className="h-5 w-5 text-sky-400 shrink-0" />
    <p>Authorized review only. Use permitted records, confirm ownership through your approved process, and keep ID images private. These checks are recorded by your team, not an automated identity or credit-bureau verification.</p>
  </div>
);

export const LoanSnapshot = ({ loan, income }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-px rounded-xl border border-white/10 bg-white/10 overflow-hidden" data-testid="review-loan-snapshot">
    {[["amount", "Loan amount", fmtPHP(loan.principal)], ["term", "Requested term", `${loan.term_months} months`], ["monthly", "Monthly repayment", fmtPHP(loan.monthly_payment)], ["income", "Reported income / month", income == null ? "Not provided" : fmtPHP(income)]].map(([key, label, value]) => (
      <div key={key} className="bg-[#111B2A] p-5"><div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div><div data-testid={`review-${key}`} className="font-mono text-base sm:text-lg font-semibold text-white mt-2 break-words">{value}</div></div>
    ))}
  </div>
);
